import React from 'react';

import {assert} from 'Asserts';
import OperatorDropdown from 'search/filters/base/OperatorDropdown';
import {Operator} from 'search/SearchConstants';
import DatetimeField from 'ui-library/inputs/DatetimeField';
import {LABEL_STYLES} from 'ui-library/inputs/InputConstants';
import pickDefined from 'util/pickDefined';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './FilterFormConstants';
import FilterFormSkeletonNodeLayout from './FilterFormSkeletonNodeLayout';
import PrimaryFilterFormSkeleton from './PrimaryFilterFormSkeleton';
import SecondaryFilterFormSkeleton from './SecondaryFilterFormSkeleton';

interface DatetimeFilterFormProps extends BaseFilterFormProps {
  filterStyle: FilterStyle;
  fromDatetime: string | null;
  onChangetoDatetime: (value: string) => void;
  onChangefromDatetime: (value: string) => void;
  onRemove?: () => void;
  toDatetime: string | null;
  onChangeOperator?: (value: Operator) => void;
  operators?: Array<Operator>;
  selectedOperator?: Operator;
}

export default function DatetimeFilterForm(props: DatetimeFilterFormProps): JSX.Element {
  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  // Display operator dropdown if there are operators to choose from
  let operatorDropdown: JSX.Element | null = null;
  if (props.operators != null && props.operators.length > 1) {
    assert(
      props.onChangeOperator != null,
      'Expected onChangeOperator to be defined when more than one operator is provided'
    );
    assert(
      props.selectedOperator != null,
      'Expected selectedOperator to be defined when more than one operator is provided'
    );

    operatorDropdown =
      props.onChangeOperator != null && props.selectedOperator != null ? (
        <OperatorDropdown
          buttonSize={props.buttonSize}
          onChangeOperator={props.onChangeOperator}
          operators={props.operators}
          selectedOperator={props.selectedOperator}
        />
      ) : null;
  }

  const fromDatetimeInput = (
    <DatetimeField
      labelStyle={LABEL_STYLES.HIDDEN}
      onChange={props.onChangefromDatetime}
      placeholder="from"
      value={props.fromDatetime}
    />
  );
  const toDatetimeInput = (
    <DatetimeField
      labelStyle={LABEL_STYLES.HIDDEN}
      onChange={props.onChangetoDatetime}
      placeholder="to"
      value={props.toDatetime}
    />
  );

  let nodeGrid: Array<Array<JSX.Element>>;
  if (operatorDropdown != null) {
    nodeGrid =
      props.selectedOperator === Operator.IS_EMPTY
        ? [[operatorDropdown]]
        : [[operatorDropdown], [fromDatetimeInput, toDatetimeInput]];
  } else {
    const emptyNode = <span />; // To get end date input to right-align beneath the start date input
    nodeGrid = [[fromDatetimeInput], [emptyNode, toDatetimeInput]];
  }
  if (props.filterStyle === FilterStyle.PRIMARY) {
    return (
      <PrimaryFilterFormSkeleton
        {...skeletonProps}
        content={<FilterFormSkeletonNodeLayout nodeGrid={[[fromDatetimeInput], [toDatetimeInput]]} />}
        dropdownToggleText={props.dropdownToggleText}
      />
    );
  } else {
    if (!props.onRemove) {
      throw new Error('Expected props.onRemove to be present when filterStyle is SECONDARY');
    }
    return (
      <SecondaryFilterFormSkeleton
        {...skeletonProps}
        nodeGrid={nodeGrid}
        onChangeFilterKey={props.onChangeFilterKey}
        selectedFilterKey={props.selectedFilterKey}
        onRemove={props.onRemove}
      />
    );
  }
}
